/**
 * capabilityProbe — runtime detection of the per-host customer auth gateway
 * (SIH-17 / DEF-B4-1 / PM Option C).
 *
 * The customer auth surface lives at /api/auth/* (ADR-003 §2.2 D2). On a
 * customer host (Mount Meru, Bank), nginx routes /api/auth/* to the gateway
 * on 127.0.0.1:9102 and the gateway's /api/auth/healthz answers 200. On
 * hosts without a gateway (e.g. ncv's parked deployment), the same path
 * returns 401/404/non-2xx — the gateway is absent.
 *
 * Detection is real (PM §2.2):
 *   - no hardcoded hostnames;
 *   - no CSS-hidden-but-reachable tab;
 *   - no special-case branch per host.
 *
 * The probe has a short timeout so a misconfigured host (e.g. nginx hangs
 * on upstream) cannot keep the user on the loading frame indefinitely.
 * The default is 2000ms — long enough for normal network variance, short
 * enough that the user perceives the login page within a second.
 *
 * Failure modes (each returns `'absent'`, never throws):
 *   - non-2xx HTTP response (gateway missing or 5xx'ing)
 *   - timeout (default 2000ms, configurable)
 *   - network error (DNS failure, connection refused, CORS error)
 *   - caller's AbortSignal already aborted (treated as "not present" so
 *     the auth store's hydrate remains idempotent on unmount)
 *
 * Timeout implementation: Promise.race against a setTimeout. The fetch
 * itself is NOT given an AbortSignal — jsdom's fetch checks the signal
 * against its own AbortSignal class and rejects Node's, which makes
 * signal-based cancellation unreliable across environments. The probe
 * resolves at timeout without waiting for the fetch; the fetch continues
 * to completion in the background (a leaked fetch is harmless because
 * the response is never used).
 *
 * Transport is funneled through apiClient.probeCustomerGatewayHealth()
 * so raw fetch lives ONLY in src/api/ — the API service boundary rule.
 *
 * The auth store's hydrate() invokes this probe in parallel with the
 * session probes; capability is a separate concern from authentication.
 */

import { probeCustomerGatewayHealth } from '@api/apiClient'

/**
 * Three-state model for the customer auth gateway on the current host
 * (SIH-17 / DEF-B4-1 / PM Option C). The LoginPage only renders the
 * Customer tab when capability is `present`; the auth store refuses to
 * call customerLogin when capability is anything other than `present`.
 *
 * `unknown` is the initial state and is shown as the loading frame by
 * the AuthGate until the probe completes.
 */
export type CustomerGatewayCapability = 'unknown' | 'present' | 'absent'

/** Default probe timeout. Long enough for real network variance; short
 * enough to keep the loading frame under one second in the bad case. */
export const CUSTOMER_GATEWAY_PROBE_TIMEOUT_MS = 2000

/**
 * Probe the host's customer auth gateway.
 *
 * Returns `'present'` when /api/auth/healthz answers 2xx; `'absent'` for
 * any non-2xx, timeout, network error, or pre-aborted signal. Never throws.
 *
 * The optional `signal` is the caller's abort — used by the auth store's
 * hydrate to cancel on unmount. A pre-aborted signal resolves to
 * `'absent'` so the store does not flash an error frame during unmount.
 */
export async function probeCustomerGateway(
  signal?: AbortSignal,
  timeoutMs: number = CUSTOMER_GATEWAY_PROBE_TIMEOUT_MS,
): Promise<CustomerGatewayCapability> {
  if (signal?.aborted === true) {
    return 'absent'
  }
  // Timeout via Promise.race. The fetch itself is intentionally unhandled
  // on timeout — the response is discarded and the network call completes
  // (or hangs) in the background. This is acceptable because:
  //   1. The same-origin request is bounded by the OS network stack.
  //   2. The probe's contract is "tell me the verdict" — the response
  //      body is never inspected.
  const fetchPromise = probeCustomerGatewayHealth()
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<Response | typeof TIMEOUT_SENTINEL>((resolve) => {
    timeoutHandle = setTimeout(() => resolve(TIMEOUT_SENTINEL), timeoutMs)
  })

  // Wire the caller's signal: when the caller aborts, the fetch is allowed
  // to keep running but we resolve immediately with TIMEOUT_SENTINEL so
  // the caller doesn't hang. (The signal itself doesn't reach fetch — see
  // the jsdom-mismatch note in apiClient.ts.)
  let signalListener: (() => void) | undefined
  const signalPromise = signal !== undefined
    ? new Promise<Response | typeof TIMEOUT_SENTINEL>((resolve) => {
        signalListener = () => resolve(TIMEOUT_SENTINEL)
        signal.addEventListener('abort', signalListener, { once: true })
      })
    : undefined

  // The verdict is 'absent' until the probe proves the gateway is there.
  // The verdict is computed inside the attempt and returned after the
  // cleanup, so the timeout handle and the abort listener are always
  // released - on the success path, on the timeout and on the failure path.
  let verdict: CustomerGatewayCapability = 'absent'
  try {
    const winner = await Promise.race([
      fetchPromise,
      timeoutPromise,
      ...(signalPromise !== undefined ? [signalPromise] : []),
    ])
    if (winner !== TIMEOUT_SENTINEL) {
      const response = winner as Response
      verdict = response.ok ? 'present' : 'absent'
    }
    // A timeout leaves the verdict at 'absent'.
  } catch {
    // Fetch rejection (network error, CORS error, TypeError on abort) —
    // every failure collapses to 'absent'. The probe never throws.
  } finally {
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle)
    if (signal !== undefined && signalListener !== undefined) {
      signal.removeEventListener('abort', signalListener)
    }
  }
  return verdict
}

/** Sentinel value for Promise.race — distinguishes "timed out" from "fetch
 * resolved with a Response". */
const TIMEOUT_SENTINEL: unique symbol = Symbol('capabilityProbe.timeout')